// VbPagoBox — "V°B° para Pago" de una OC (Iter 413).
// Muestra la conformidad del solicitante para habilitar el pago: estado
// (Autorizada / Pendiente / No aplica), solicitante(s), requisición(es),
// fecha y la aprobación electrónica o una línea para firma manual.
// Si `editable`, permite otorgar o revertir el V°B° (PUT /compras/oc/{n}/vb-pago).
import { useState } from 'react';
import { ShieldCheck, Clock, MinusCircle, Pencil, Save, X, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { fmtFecha } from '../lib/utils';
import api from '../lib/api';

const ESTADO_STYLES = {
  autorizada: {
    chip: 'bg-emerald-100 text-emerald-700 border-emerald-300',
    box: 'border-emerald-300 bg-emerald-50/60',
    title: 'text-emerald-800',
    icon: ShieldCheck,
    label: 'Autorizada para pago',
  },
  pendiente: {
    chip: 'bg-amber-100 text-amber-800 border-amber-300',
    box: 'border-amber-300 bg-amber-50/60',
    title: 'text-amber-800',
    icon: Clock,
    label: 'Pendiente V°B° del solicitante',
  },
  no_aplica: {
    chip: 'bg-gray-100 text-gray-600 border-gray-300',
    box: 'border-gray-300 bg-gray-50/70',
    title: 'text-gray-700',
    icon: MinusCircle,
    label: 'No aplica',
  },
};

const hoy = () => new Date().toISOString().slice(0, 10);

export function VbPagoBox({ vb, editable = false, numeroOc, onUpdated, variant = 'viewer' }) {
  const [editing, setEditing] = useState(false);
  const [fecha, setFecha] = useState(hoy());
  const [obs, setObs] = useState('');
  const [saving, setSaving] = useState(false);

  if (!vb) return null;

  const estado = (vb.estado || 'pendiente').toLowerCase();
  const st = ESTADO_STYLES[estado] || ESTADO_STYLES.pendiente;
  const Icon = st.icon;
  const isViewer = variant === 'viewer';

  const solicitantes = (vb.solicitantes && vb.solicitantes.length)
    ? vb.solicitantes.join(', ')
    : (vb.solicitante_oc || 'Sin requisición asociada');
  const requisiciones = (vb.requisiciones && vb.requisiciones.length)
    ? vb.requisiciones.join(', ')
    : '—';

  // Se puede otorgar el V°B° si está pendiente, o si es "no aplica" por ser anterior
  // a la mejora (grandfathered, solo Gerencia lo autoriza — el backend lo valida).
  const puedeAprobar = editable && !vb.aprobado && (estado === 'pendiente' || vb.grandfathered);
  const puedeRevertir = editable && vb.aprobado;

  const guardar = async (aprobado) => {
    setSaving(true);
    try {
      const body = aprobado ? { aprobado: true, fecha, observaciones: obs } : { aprobado: false, observaciones: obs };
      const res = await api.put(`/compras/oc/${numeroOc}/vb-pago`, body);
      onUpdated?.(res.data);
      setEditing(false);
      setObs('');
      toast.success(aprobado ? 'V°B° para Pago otorgado' : 'V°B° para Pago revertido');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo actualizar el V°B° para Pago');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className={`rounded border ${st.box} ${isViewer ? 'mx-3 my-2 px-3 py-2' : 'p-4'}`}
      data-testid="vb-pago-box"
    >
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <div className="flex items-center gap-2">
          <Icon className={`${isViewer ? 'w-3.5 h-3.5' : 'w-4 h-4'} ${st.title}`} />
          <span className={`font-bold uppercase tracking-wide ${isViewer ? 'text-[9px]' : 'text-xs'} ${st.title}`}>
            V°B° para Pago
          </span>
          <span
            className={`inline-flex items-center rounded-full border px-2 py-0.5 font-bold ${isViewer ? 'text-[8px]' : 'text-[10px]'} ${st.chip}`}
            data-testid="vb-pago-estado"
          >
            {vb.estado_label || st.label}
          </span>
        </div>
        {puedeAprobar && !editing && (
          <button
            type="button"
            onClick={() => { setFecha(hoy()); setObs(''); setEditing(true); }}
            className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-700 hover:text-emerald-900"
            data-testid="vb-pago-dar-btn"
          >
            <Pencil className="w-3 h-3" /> Dar V°B° para Pago
          </button>
        )}
        {puedeRevertir && !editing && (
          <button
            type="button"
            onClick={() => { setObs(''); guardar(false); }}
            disabled={saving}
            className="inline-flex items-center gap-1 text-[10px] font-medium text-gray-500 hover:text-red-700"
            data-testid="vb-pago-revertir-btn"
          >
            <RotateCcw className="w-3 h-3" /> Revertir
          </button>
        )}
      </div>

      <div className={`grid grid-cols-2 gap-2 ${isViewer ? 'text-[9px]' : 'text-xs'}`}>
        <div>
          <span className="font-semibold text-gray-500">Solicitante: </span>
          <span className="text-gray-800" data-testid="vb-pago-solicitante">{solicitantes}</span>
        </div>
        <div>
          <span className="font-semibold text-gray-500">Requisición: </span>
          <span className="text-gray-800" data-testid="vb-pago-requisicion">{requisiciones}</span>
        </div>
      </div>

      <div className={`mt-1 ${isViewer ? 'text-[9px]' : 'text-xs'}`}>
        {vb.aprobado ? (
          <p className="text-emerald-800" data-testid="vb-pago-aprobado-por">
            Aprobado electrónicamente por <b>{vb.aprobado_por_nombre || vb.aprobado_por}</b>
            {vb.aprobado_fecha ? <> el <b>{fmtFecha(vb.aprobado_fecha)}</b></> : null}
          </p>
        ) : estado === 'no_aplica' ? (
          <p className="text-gray-500 italic" data-testid="vb-pago-no-aplica-motivo">
            {vb.motivo_no_aplica === 'pago_inmediato'
              ? 'No requiere V°B°: pago al contado / tarjeta de crédito.'
              : 'No requiere V°B°: OC anterior a esta mejora.'}
          </p>
        ) : (
          <p className="text-amber-700 italic" data-testid="vb-pago-pendiente-hint">
            Falta la conformidad del solicitante para habilitar el pago.
          </p>
        )}
      </div>

      {vb.observaciones && !editing && (
        <p className={`mt-1 ${isViewer ? 'text-[9px]' : 'text-xs'} text-gray-700`} data-testid="vb-pago-observaciones">
          <span className="font-semibold text-gray-500">Observaciones: </span>{vb.observaciones}
        </p>
      )}

      {editing && (
        <div className="mt-2 space-y-1.5" data-testid="vb-pago-form">
          <div className="flex items-center gap-2">
            <label className="text-[11px] font-medium text-gray-600">Fecha:</label>
            <input
              type="date"
              value={fecha}
              onChange={(e) => setFecha(e.target.value)}
              className="rounded border border-gray-300 px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-emerald-500"
              data-testid="vb-pago-fecha-input"
            />
          </div>
          <textarea
            value={obs}
            onChange={(e) => setObs(e.target.value)}
            rows={2}
            maxLength={500}
            className="w-full rounded border border-gray-300 px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-emerald-500"
            placeholder="Observaciones (opcional): recepción conforme, con reparos, etc."
            data-testid="vb-pago-obs-input"
          />
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => guardar(true)}
              disabled={saving}
              className="inline-flex items-center gap-1 rounded bg-emerald-600 px-2.5 py-1 text-[11px] font-medium text-white hover:bg-emerald-700 disabled:opacity-60"
              data-testid="vb-pago-confirmar-btn"
            >
              <Save className="w-3 h-3" /> {saving ? 'Guardando…' : 'Confirmar V°B°'}
            </button>
            <button
              type="button"
              onClick={() => { setEditing(false); setObs(''); }}
              className="inline-flex items-center gap-1 rounded border border-gray-300 px-2.5 py-1 text-[11px] font-medium text-gray-600 hover:bg-gray-50"
              data-testid="vb-pago-cancelar-btn"
            >
              <X className="w-3 h-3" /> Cancelar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
